/**
 * Route color scheme based on route type
 * Maps route types to their corresponding colors in different states
 */

export type RouteType = "Cheapest" | "Shortest" | "Fastest" | "Other"

export type RouteColorScheme = {
  main: string // Normal state color
  hover: string // Hover state casing color
  selected: string // Selected state casing color
}

/**
 * Color definitions for each route type
 * Based on Tailwind color palette
 */
const ROUTE_COLORS: Record<RouteType, RouteColorScheme> = {
  Cheapest: {
    main: "#10b981", // Emerald 600
    hover: "#047857", // Emerald 700
    selected: "#2563EB", // Blue 600
  },
  Shortest: {
    main: "#0284c7", // Sky 600
    hover: "#0369a1", // Sky 700
    selected: "#2563EB", // Blue 600
  },
  Fastest: {
    main: "#c026d3", // Fuchsia 600
    hover: "#a21caf", // Fuchsia 700
    selected: "#2563EB", // Blue 600
  },
  Other: {
    main: "#9ca3af", // Gray 400
    hover: "#4b5563", // Gray 600
    selected: "#2563EB", // Blue 600
  },
}

/**
 * Determines route type from route label
 * Maps common route labels to their route types
 */
export function getRouteTypeFromLabel(label: string | undefined): RouteType {
  if (!label) return "Other"

  const lowerLabel = label.toLowerCase()

  // Map "Recommended" to "Fastest" as per user requirement
  if (lowerLabel.includes("recommended") || lowerLabel.includes("fastest")) {
    return "Fastest"
  }

  if (lowerLabel.includes("cheapest") || lowerLabel.includes("economic")) {
    return "Cheapest"
  }

  if (lowerLabel.includes("shortest")) {
    return "Shortest"
  }

  return "Other"
}

/**
 * Gets the color scheme for a specific route type
 */
export function getRouteColors(routeType: RouteType): RouteColorScheme {
  return ROUTE_COLORS[routeType]
}

/**
 * Gets the color scheme from a route label
 */
export function getRouteColorsFromLabel(
  label: string | undefined,
): RouteColorScheme {
  const routeType = getRouteTypeFromLabel(label)
  return getRouteColors(routeType)
}
